import { Routes } from '../../navigation/config/routes'

export const UNIT_FILE_TABS = [
    'timeline',
    'leases',
    'requests',
    'inspections',
    'work',
] as const

export const PERSON_FILE_TABS = [
    'timeline',
    'leases',
    'requests',
    'leasing',
    'notes',
] as const

export type UnitFileTab = typeof UNIT_FILE_TABS[number]
export type PersonFileTab = typeof PERSON_FILE_TABS[number]

export const OCCUPANCY_FILTERS = ['occupied', 'future', 'vacant'] as const

export type RecordsOccupancyFilter = typeof OCCUPANCY_FILTERS[number]

export const PEOPLE_COHORTS = [
    'current',
    'future',
    'past',
    'cancelled',
    'incoming',
] as const

export type RecordsPeopleCohort = typeof PEOPLE_COHORTS[number]

export type RecordsSearchState = {
    q: string
    category: string | null
    open: boolean
    occupancy: RecordsOccupancyFilter | null
    cohort: RecordsPeopleCohort | null
    tab: string | null
    kinds: string | null
    page: number
}

export const recordsUnitPath = (unitId: number) =>
    Routes.apartment.records.unit.replace(':unitId', String(unitId))

export const recordsPersonPath = (userId: number) =>
    Routes.apartment.records.person.replace(':userId', String(userId))

export const recordsLeadPath = (leadId: number) =>
    Routes.apartment.records.lead.replace(':leadId', String(leadId))

export const recordsPersonHref = (person: {
    user_id?: number | null
    lead_id?: number | null
}) => {
    if (person.user_id != null) return recordsPersonPath(person.user_id)
    if (person.lead_id != null) return recordsLeadPath(person.lead_id)
    return Routes.apartment.records.people
}

export const parseRecordsSearch = (search: string): RecordsSearchState => {
    const params = new URLSearchParams(search)
    const occupancy = params.get('occupancy')
    const cohort = params.get('cohort')
    const rawPage = params.get('page')
    const parsedPage = rawPage ? Number.parseInt(rawPage, 10) : 1
    return {
        q: params.get('q') || '',
        category: params.get('category'),
        open: params.get('open') === '1',
        occupancy:
            occupancy &&
            (OCCUPANCY_FILTERS as readonly string[]).includes(occupancy)
                ? (occupancy as RecordsOccupancyFilter)
                : null,
        cohort:
            cohort && (PEOPLE_COHORTS as readonly string[]).includes(cohort)
                ? (cohort as RecordsPeopleCohort)
                : null,
        tab: params.get('tab'),
        kinds: params.get('kinds'),
        page: Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1,
    }
}

export const buildRecordsSearch = (
    opts: Partial<RecordsSearchState>,
): string => {
    const params = new URLSearchParams()
    const q = (opts.q || '').trim()
    if (q) params.set('q', q)
    if (opts.category) params.set('category', opts.category)
    if (opts.open) params.set('open', '1')
    if (opts.occupancy) params.set('occupancy', opts.occupancy)
    if (opts.cohort && opts.cohort !== 'current')
        params.set('cohort', opts.cohort)
    if (opts.tab && opts.tab !== 'timeline') params.set('tab', opts.tab)
    if (opts.kinds) params.set('kinds', opts.kinds)
    if (opts.page && opts.page > 1) params.set('page', String(opts.page))
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

export const mergeRecordsSearch = (
    currentSearch: string,
    updates: Partial<RecordsSearchState> = {},
    drop: Array<keyof RecordsSearchState> = [],
): string => {
    const current = parseRecordsSearch(currentSearch)
    const next: RecordsSearchState = { ...current, ...updates }
    drop.forEach((key) => {
        if (key === 'open') next.open = false
        else if (key === 'q') next.q = ''
        else if (key === 'page') next.page = 1
        else next[key] = null
    })
    return buildRecordsSearch(next)
}

export const parseUnitFileTab = (tab: string | null): UnitFileTab => {
    if (tab && (UNIT_FILE_TABS as readonly string[]).includes(tab)) {
        return tab as UnitFileTab
    }
    return 'timeline'
}

export const parsePersonFileTab = (tab: string | null): PersonFileTab => {
    if (tab && (PERSON_FILE_TABS as readonly string[]).includes(tab)) {
        return tab as PersonFileTab
    }
    return 'timeline'
}
